/**
 * Centralized utilities for managing embedded session lifecycle
 * All localStorage operations for embedded sessions should go through here
 */

export const EMBEDDED_SESSION_KEY = 'convopro-embedded-session';

export interface EmbeddedSessionData {
  sessionId: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    name: string;
    username?: string;
    isAdmin?: boolean;
  };
}

/**
 * Clear the embedded session from localStorage
 * @param caller - Identifier of the calling function/component for debugging
 * @param reason - Optional reason for clearing
 */
export function clearEmbeddedSession(caller: string, reason?: string): void {
  if (typeof window === 'undefined') return;
  
  const hadSession = !!localStorage.getItem(EMBEDDED_SESSION_KEY);
  
  if (hadSession) {
    console.warn(`[EmbeddedSession] 🗑️ CLEARED by: ${caller}${reason ? ` - Reason: ${reason}` : ''}`);
    localStorage.removeItem(EMBEDDED_SESSION_KEY);
  }
}

/**
 * Get the embedded session from localStorage
 * @returns The parsed session object or null if not found/invalid
 */
export function getEmbeddedSession(): EmbeddedSessionData | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const sessionStr = localStorage.getItem(EMBEDDED_SESSION_KEY);
    
    // EDGE FIX: Verify storage access is working
    // Edge can return null even when session exists if storage is partitioned
    if (sessionStr === null) {
      // Test if localStorage is actually accessible
      try {
        const testKey = '__storage_access_test__';
        localStorage.setItem(testKey, '1');
        localStorage.removeItem(testKey);
        // Storage works, session truly doesn't exist
        return null;
      } catch (accessError) {
        // Storage is blocked - this is the Edge issue
        console.error('[EmbeddedSession] ❌ EDGE: localStorage access denied by browser', accessError);
        throw new Error('Storage access denied by browser - session may exist but is inaccessible');
      }
    }
    
    if (sessionStr === 'null' || sessionStr === 'undefined') {
      console.warn('[EmbeddedSession] Found invalid session string:', sessionStr);
      return null;
    }
    
    return JSON.parse(sessionStr) as EmbeddedSessionData;
  } catch (error) {
    // Only log parse errors, not the access test error from above
    if (error instanceof Error && !error.message.includes('Storage access denied')) {
      console.error('[EmbeddedSession] Failed to read session:', error);
    }
    return null;
  }
}

/**
 * Set the embedded session in localStorage
 * @param session - The session object to store
 * @param caller - Identifier of the calling function/component for debugging
 */
export function setEmbeddedSession(session: EmbeddedSessionData, caller: string): void {
  if (typeof window === 'undefined') return;
  
  console.log(`[EmbeddedSession] 💾 SET by: ${caller} - SessionId: ${session.sessionId?.substring(0, 20)}...`);
  localStorage.setItem(EMBEDDED_SESSION_KEY, JSON.stringify(session));
}

/**
 * Check if a session is valid (not expired)
 * @param session - The session object to check
 * @returns true if session exists and is not expired
 */
export function isSessionValid(session: EmbeddedSessionData | null): boolean {
  if (!session?.sessionId || !session?.expiresAt) return false;
  
  const expiresAt = new Date(session.expiresAt);
  const now = new Date();
  return expiresAt.getTime() > now.getTime();
}
